import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnDestroy, signal, untracked, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { forkJoin, take } from 'rxjs';
import { Task } from '../../shared/entities/task/task';
import { EditSelectV2InputComponent } from '../../../core/components/edit-select-v2-form-control/edit-select-v2-form-control.component';
import { WidgetCoordinatorService } from '../../../core/services/issue/widget-coordinator.service';
import { User } from '../../../core/entities/user/user';
import { UsersService } from '../../../core/services/users/users.service';
import { CommandInput, CommandTemplate } from '../../../core/entities/commandTemplate/commandTemplate';
import { CommandService, Reloadable } from '../../../core/services/command/command.service';
import { RefetchCoordinator } from '../../../core/services/refetch-coordinator/refetch-coordinator.service';
import { LocationService } from '../../shared/services/location/location.service';

@Component({
  selector: 'nx-assign-task-widget',
  templateUrl: './assign-task-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssignTaskWidgetComponent implements Reloadable, OnDestroy {

  // Injected services
  private readonly commandService = inject(CommandService);
  private readonly coordinator = inject(WidgetCoordinatorService);
  private readonly refetchCoordinator = inject(RefetchCoordinator);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locationService = inject(LocationService);
  private readonly usersService: UsersService = inject(UsersService);

  // Signal inputs
  readonly task = input.required<Task>();

  // View child signal
  readonly selectComponent = viewChild<EditSelectV2InputComponent>('selectComponent');

  // State signals
  readonly editMode = signal(false);
  readonly isLoading = signal(false);
  readonly commandTemplate = signal<CommandTemplate | undefined>(undefined);
  readonly assignee = signal<{ id: string; displayName: string; assignmentType: string } | undefined>(undefined);
  readonly options = signal<Array<{ id: string; label: string; items: any[] }>>([])

  // Form controls
  readonly selectedControl = new FormControl<string | null>(null);
  readonly form = new FormGroup({
    selected: this.selectedControl
  });

  // Widget ID for coordinator
  private readonly widgetId = `assign-task-widget-${Math.random().toString(36).substr(2, 9)}`;
  private unregisterRefetch?: () => void;

  constructor() {
    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });

    // Effect to handle task changes and load command template
    effect(() => {
      const currentTask = this.task();
      if (currentTask?.id) {
        // Register with RefetchCoordinator in untracked to avoid circular dependencies
        untracked(() => {
          this.unregisterRefetch?.();
          this.unregisterRefetch = this.refetchCoordinator.register(
            this,
            currentTask.getEntityType(),
            currentTask.id
          );
        });
        
        // Load command template when task changes
        this.loadCommandTemplate();
      }
    });
  }

  loadCommandTemplate() {
    const currentTask = this.task();
    if (!currentTask?.id) return;

    const commandInput = new CommandInput();
    commandInput.name = "taskAssignCommand";
    commandInput.addParameter('commandProviderId', currentTask.id);
      
    this.commandService.getCommandByCommandInput(commandInput)
        .pipe(take(1))
        .subscribe(result => {
          const template = result.data.getCommandByCommandInput;
          this.commandTemplate.set(template);
          
          if (template?.flow?.settings?.customFlowSettings?.assignee) {
            const assigneeData = template.flow.settings.customFlowSettings.assignee;
            this.assignee.set({
              id: assigneeData.id,
              displayName: assigneeData.label,
              assignmentType: assigneeData.type
            } as any);
          } else {
            this.assignee.set(undefined);
          }
          
          this.isLoading.set(false);
        });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode.set(true);
    
    const currentAssignee = this.assignee();
    if (currentAssignee) {
      this.selectedControl.setValue(currentAssignee.id);
    }
    
    // Load locations and active users in parallel
    forkJoin({
      locations: this.locationService.getLocations(),
      activeUsers: this.usersService.getActiveUsers()
    }).pipe(take(1))
      .subscribe(({ locations, activeUsers }) => {
        const groups = [];
        
        if (locations.length > 0) {
          groups.push({
            id: 'LocationTaskAssignmentStrategy',
            label: 'Standorte',
            items: locations.map(loc => ({
              id: loc.id,
              name: loc.name
            }))
          });
        }
        
        if (activeUsers.length > 0) {
          groups.push({
            id: 'UserTaskAssignmentStrategy',
            label: 'Benutzer',
            items: (activeUsers as User[]).map(user => ({
              id: user.id,
              name: user.displayName
            }))
          });
        }
        
        this.options.set(groups);
      });
    
    setTimeout(() => {
      this.selectComponent()?.openAndFocusFilter();
    }, 10);
  }

  save() {
    // Extract the group that contains the selected value BEFORE clearing options
    const selectedValue = this.selectedControl.value;
    let assignmentStrategy: string | undefined;
    
    if (selectedValue) {
      const currentOptions = this.options();
      for (const group of currentOptions) {
        const foundItem = group.items.find(item => item.id === selectedValue);
        if (foundItem) {
          assignmentStrategy = group.id;
          break;
        }
      }
    }
    
    this.editMode.set(false);
    this.options.set([]);
    
    this.executeAssignCommand(selectedValue, assignmentStrategy);
  }

  cancel() {
    this.editMode.set(false);
    this.options.set([]);
  }

  reload(): void {
    this.loadCommandTemplate();
  }

  private executeAssignCommand(assigneeId: string | null | undefined, assignmentType: string | undefined) {
    const currentTask = this.task();
    const template = this.commandTemplate();
    if (!currentTask?.id || !template?.name) return;

    const commandInput = new CommandInput();
    commandInput.name = template.name;
    commandInput.addParameter('commandProviderId', currentTask.id);
    commandInput.addParameter('assigneeId', assigneeId);
    commandInput.addParameter('assignmentType', assignmentType);
    
    this.isLoading.set(true);
    this.commandService.sendCommand(commandInput, (result) => {
      this.loadCommandTemplate();
      this.refetchCoordinator.emit({ 
        entityType: currentTask.getEntityType(), 
        entityId: currentTask.id 
      });
    });
  }

  acceptSuggestion() {
    const currentTask = this.task();
    const assigneeId = currentTask?.assignmentSuggestion?.assigneeId;
    const assignmentType = currentTask?.assignmentSuggestion?.assignmentType;
    
    this.executeAssignCommand(assigneeId, assignmentType);
  }

}
